/**
 * Deterministic per-company gradient palette for the generated logo fallback tile.
 * Same company key always lands on the same theme (FNV-1a hash of the name).
 * Kept clear of purple (~255-290°) so nothing here is ever confused with the
 * reserved Työmarkkinatori treatment in CompanyLogoScriptFallback.
 */

export type CompanyLogoPaletteTheme = {
  name: string;
  deep: string;
  mid: string;
  light: string;
};

export const COMPANY_LOGO_PALETTE_THEMES: CompanyLogoPaletteTheme[] = [
  { name: 'forest', deep: '#24392c', mid: '#3b5e45', light: '#5a8a68' },
  { name: 'teal', deep: '#12292a', mid: '#2f5a58', light: '#4a8583' },
  { name: 'amber', deep: '#4a3200', mid: '#6e4f00', light: '#b8860b' },
  { name: 'terracotta', deep: '#3d1810', mid: '#6b3527', light: '#9a4f3c' },
  { name: 'harbor', deep: '#122a3d', mid: '#274d6b', light: '#4a7fa3' },
  { name: 'stone', deep: '#2e2a24', mid: '#4d453a', light: '#766a56' },
  { name: 'moss', deep: '#1c2e1a', mid: '#33502e', light: '#6b8f4e' },
];

function hashCompanyKey(key: string): number {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = (hash * 16777619) >>> 0;
  }
  return hash;
}

/** Picks a palette theme deterministically from a company key (name or id). */
export function companyLogoPaletteThemeFor(key: string): CompanyLogoPaletteTheme {
  const normalized = key.trim().toLowerCase();
  const index = hashCompanyKey(normalized) % COMPANY_LOGO_PALETTE_THEMES.length;
  return COMPANY_LOGO_PALETTE_THEMES[index];
}

/** Same 152deg three-stop gradient shape used across the fallback tile's themes. */
export function companyLogoPaletteGradient(theme: CompanyLogoPaletteTheme): string {
  return `linear-gradient(152deg, ${theme.deep} 0%, ${theme.mid} 46%, ${theme.light} 100%)`;
}
